export interface Tokens {
  corPrimaria: string; corDestaque: string; corSecundaria: string; fundo: string; superficie: string
  texto: string; textoSuave: string; borda: string; positivo: string; alerta: string; negativo: string
  fonte: string; fonteUrl?: string | null; raio: number; espaco: number; paleta: string[]
}

export interface Template { id?: number | null; nome: string; descricao?: string | null; tokens: Tokens; css: string; logo?: string | null; padrao?: boolean }

/** Arquivo grande demais para ser um template: o CSS aceita 100 KB e o logo 300 KB. */
export const MAX_BYTES_DO_ARQUIVO = 600_000

// As mesmas regras do servidor (TemplatesHtml.Validar). O servidor continua sendo quem decide ao salvar;
// aqui o objetivo é listar todos os problemas de uma vez, para o arquivo ser corrigido numa volta só.
const MAX_CSS = 100_000
const MAX_LOGO = 300_000
const MAX_CORES_PALETA = 12
const HEX = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/
const PILHA_DE_FONTES = /^[A-Za-z0-9 ,'-]{1,200}$/
const LOGO_EMBUTIDO = /^data:image\/(png|jpeg|webp|svg\+xml);base64,[A-Za-z0-9+/=]+$/
const TAG_PROIBIDA_NO_CSS = /<\s*\/?\s*(style|script)\b|<!--/i

export const CAMPOS_DE_COR: { chave: keyof Tokens; rotulo: string }[] = [
  { chave: 'corPrimaria', rotulo: 'Cor primária' }, { chave: 'corDestaque', rotulo: 'Cor de destaque' },
  { chave: 'corSecundaria', rotulo: 'Cor secundária' }, { chave: 'fundo', rotulo: 'Fundo' },
  { chave: 'superficie', rotulo: 'Superfície' }, { chave: 'texto', rotulo: 'Texto' },
  { chave: 'textoSuave', rotulo: 'Texto suave' }, { chave: 'borda', rotulo: 'Borda' },
  { chave: 'positivo', rotulo: 'Positivo' }, { chave: 'alerta', rotulo: 'Alerta' }, { chave: 'negativo', rotulo: 'Negativo' }
]

const CAMPOS_DO_TEMPLATE = ['nome', 'descricao', 'tokens', 'css', 'logo']
// "id" e "padrao" são ignorados de propósito: o template importado entra como novo e nunca vira o padrão sozinho.
const CAMPOS_IGNORADOS_SEM_AVISO = ['id', 'padrao']
const CAMPOS_DOS_TOKENS = [...CAMPOS_DE_COR.map(c => c.chave), 'fonte', 'fonteUrl', 'raio', 'espaco', 'paleta']

export type ResultadoDaImportacao =
  | { ok: true; template: Template; avisos: string[] }
  | { ok: false; problemas: string[] }

const visto = (v: unknown) => {
  const texto = typeof v === 'string' ? v : JSON.stringify(v)
  const curto = (texto ?? 'vazio').slice(0, 30)
  return texto && texto.length > 30 ? `${curto}…` : curto
}

const ehObjeto = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

/**
 * Lê o texto de um arquivo de template (.json) e devolve o template pronto para preencher o formulário, ou a lista
 * completa do que está errado. Não grava nada: quem importa revisa a prévia e salva.
 */
export function lerTemplate(conteudo: string): ResultadoDaImportacao {
  const texto = conteudo.replace(/^﻿/, '').trim()
  if (!texto) return { ok: false, problemas: ['O arquivo está vazio.'] }

  let bruto: unknown
  try {
    bruto = JSON.parse(texto)
  } catch (e: any) {
    return { ok: false, problemas: [`O arquivo não é um JSON válido (${e?.message || 'erro de leitura'}). Peça ao Claude um JSON puro, sem texto antes ou depois.`] }
  }
  if (!ehObjeto(bruto)) return { ok: false, problemas: ['O arquivo precisa ser um objeto JSON com nome, tokens, css e logo.'] }

  const problemas: string[] = []

  const nome = typeof bruto.nome === 'string' ? bruto.nome.trim() : ''
  if (!nome || nome.length > 80) problemas.push('Nome: dê um nome ao template, com até 80 caracteres.')

  const descricao = bruto.descricao == null ? '' : bruto.descricao
  if (typeof descricao !== 'string') problemas.push('Descrição: precisa ser um texto.')
  else if (descricao.length > 1000) problemas.push(`Descrição: aceita até 1000 caracteres (veio com ${descricao.length}).`)

  const tokens = ehObjeto(bruto.tokens) ? bruto.tokens : null
  if (!tokens) problemas.push('Tokens: faltam as cores, a fonte e as medidas (o campo "tokens").')

  if (tokens) {
    CAMPOS_DE_COR.forEach(({ chave, rotulo }) => {
      const valor = tokens[chave]
      if (typeof valor !== 'string' || !HEX.test(valor)) problemas.push(`${rotulo} (${chave}): use uma cor em hexadecimal, como #7b1fa2 (veio ${visto(valor)}).`)
    })

    if (typeof tokens.fonte !== 'string' || !PILHA_DE_FONTES.test(tokens.fonte))
      problemas.push(`Fonte: use só nomes de fonte separados por vírgula, com apóstrofo nos nomes com espaço, como 'Nunito Sans', sans-serif (veio ${visto(tokens.fonte)}).`)

    const url = tokens.fonteUrl
    if (url != null && url !== '' && (typeof url !== 'string' || !url.startsWith('https://fonts.googleapis.com/') || url.length > 500 || /["<> ]/.test(url)))
      problemas.push('Endereço da fonte (fonteUrl): precisa ser uma folha do Google Fonts (https://fonts.googleapis.com/...), sem espaços, ou null.')

    if (!Number.isInteger(tokens.raio) || (tokens.raio as number) < 0 || (tokens.raio as number) > 40) problemas.push(`Arredondamento (raio): um número inteiro de 0 a 40 (veio ${visto(tokens.raio)}).`)
    if (!Number.isInteger(tokens.espaco) || (tokens.espaco as number) < 4 || (tokens.espaco as number) > 40) problemas.push(`Espaçamento (espaco): um número inteiro de 4 a 40 (veio ${visto(tokens.espaco)}).`)

    const paleta = tokens.paleta
    if (!Array.isArray(paleta) || paleta.length === 0 || paleta.length > MAX_CORES_PALETA) problemas.push(`Paleta dos gráficos: de 1 a ${MAX_CORES_PALETA} cores em hexadecimal.`)
    else {
      const ruins = paleta.map((c, i) => ({ c, i })).filter(({ c }) => typeof c !== 'string' || !HEX.test(c))
      if (ruins.length) problemas.push(`Paleta dos gráficos: cor ${ruins.map(r => `${r.i + 1} (${visto(r.c)})`).join(', ')} não está em hexadecimal.`)
    }
  }

  const css = bruto.css
  if (typeof css !== 'string' || !css.trim()) problemas.push('CSS: falta o CSS dos componentes. O CSS do template substitui o padrão por completo.')
  else {
    if (css.length > MAX_CSS) problemas.push(`CSS: passa de ${MAX_CSS / 1000} KB (tem ${Math.round(css.length / 1000)} KB, contando imagens embutidas).`)
    if (TAG_PROIBIDA_NO_CSS.test(css)) problemas.push('CSS: não pode ter <style>, <script> nem comentário HTML; escreva só as regras.')
  }

  const logo = bruto.logo == null || bruto.logo === '' ? null : bruto.logo
  if (logo !== null && (typeof logo !== 'string' || logo.length > MAX_LOGO || !LOGO_EMBUTIDO.test(logo)))
    problemas.push(`Logo: use null ou uma imagem embutida (data:image/png;base64,...) PNG, JPG, WEBP ou SVG de até ${MAX_LOGO / 1000} KB.`)

  if (problemas.length) return { ok: false, problemas }

  const t = tokens as Record<string, any>
  const avisos: string[] = []
  const sobrando = Object.keys(bruto).filter(k => !CAMPOS_DO_TEMPLATE.includes(k) && !CAMPOS_IGNORADOS_SEM_AVISO.includes(k))
  if (sobrando.length) avisos.push(`Campos que o portal não usa foram ignorados: ${sobrando.join(', ')}.`)
  const tokensSobrando = Object.keys(t).filter(k => !CAMPOS_DOS_TOKENS.includes(k))
  if (tokensSobrando.length) avisos.push(`Tokens que o portal não usa foram ignorados: ${tokensSobrando.join(', ')}.`)

  const template: Template = {
    id: null,
    nome,
    descricao: descricao as string,
    tokens: {
      corPrimaria: t.corPrimaria, corDestaque: t.corDestaque, corSecundaria: t.corSecundaria, fundo: t.fundo,
      superficie: t.superficie, texto: t.texto, textoSuave: t.textoSuave, borda: t.borda, positivo: t.positivo,
      alerta: t.alerta, negativo: t.negativo, fonte: t.fonte, fonteUrl: t.fonteUrl ? t.fonteUrl : null,
      raio: t.raio, espaco: t.espaco, paleta: [...t.paleta]
    },
    css: css as string,
    logo: logo as string | null,
    padrao: false
  }
  return { ok: true, template, avisos }
}

/** Baixa um texto como arquivo, em UTF-8 e sem passar por link do servidor. */
export function baixarTexto(nomeArquivo: string, conteudo: string, tipo = 'text/markdown;charset=utf-8') {
  const url = URL.createObjectURL(new Blob([conteudo], { type: tipo }))
  const link = document.createElement('a')
  link.href = url
  link.download = nomeArquivo
  document.body.appendChild(link)
  link.click()
  link.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

const semAcentos = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

/**
 * O template como arquivo .json, no mesmo formato que a importação lê e que o guia ensina o Claude a entregar. Serve
 * para ajustar um template existente com o Claude (entregue este arquivo junto com o guia) e para guardar uma cópia.
 */
export function templateParaArquivo(t: Template): { nomeArquivo: string; conteudo: string } {
  const k = t.tokens
  const objeto = {
    nome: t.nome,
    descricao: t.descricao ?? '',
    tokens: {
      corPrimaria: k.corPrimaria, corDestaque: k.corDestaque, corSecundaria: k.corSecundaria, fundo: k.fundo, superficie: k.superficie,
      texto: k.texto, textoSuave: k.textoSuave, borda: k.borda, positivo: k.positivo, alerta: k.alerta, negativo: k.negativo,
      fonte: k.fonte, fonteUrl: k.fonteUrl ? k.fonteUrl : null, raio: k.raio, espaco: k.espaco, paleta: [...k.paleta]
    },
    css: t.css,
    logo: t.logo ? t.logo : null
  }
  const nome = semAcentos(t.nome || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60) || 'template'
  return { nomeArquivo: `template-${nome}.json`, conteudo: JSON.stringify(objeto, null, 2) + '\n' }
}
